<template>
  <b-modal
    id="upload-image-modal"
    ref="uploadImageModalRef"
    size="lg"
    :title="modalTitle"
    @ok="handleOk"
    :busy="busyButton"
  >
    <div class="d-flex flex-column" style="gap: 1rem;">
      <b-form-file
        ref="imageInputRef"
        :accept="accept"
        :multiple="multiple"
        :placeholder="placeholder"
        :drop-placeholder="dropPlaceholder"
        :browse-text="browseText"
        @change="onImagesSelected"
      />
      <div class="images-list-container">
        <div
          v-for="(image, index) in previewImages"
          :key="index"
          class="image-wrapper"
          :class="{ 'image-wrapper-zoomed': zoomedIndex === index }"
        >
          <img
            :src="image.base64Url"
            alt="Preview da imagem"
            class="preview-image"
            :class="{ zoomed: zoomedIndex === index }"
            @click="toggleZoom(index)"
          />
          <button
            v-if="showDownloadButton"
            type="button"
            class="btn btn-sm btn-success download-image-btn"
            @click="downloadImage(index)"
            :class="{ hidden: zoomedIndex === index }"
          >
            <b-icon-download />
          </button>
          <button
            v-if="showRemoveButton"
            type="button"
            class="btn btn-sm btn-danger remove-image-btn"
            @click="removeImage(index)"
            :class="{ hidden: zoomedIndex === index }"
          >
            <b-icon-trash-fill />
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { Api } from '../../vue/api';
const API = new Api();

export default {
  name: 'UploadImageModal',
  props: {
    modalTitle: {
      type: String,
      default: 'Fazer upload de imagem',
    },
    accept: {
      type: String,
      default: 'image/*',
    },
    multiple: {
      type: Boolean,
      default: true, // Permite múltiplos arquivos
    },
    placeholder: {
      type: String,
      default: 'Escolha ou solte uma ou mais imagens...',
    },
    dropPlaceholder: {
      type: String,
      default: 'Solte as imagens aqui...',
    },
    browseText: {
      type: String,
      default: 'Escolher',
    },
    showDownloadButton: {
      type: Boolean,
      default: true,
    },
    showRemoveButton: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      previewImages: [],
      busyButton: false,
      zoomedIndex: null,
    };
  },

  methods: {
    openModal(initialImages = []) {
      this.previewImages = initialImages.map(url => ({
        remoteUrl: url,
        base64Url: null,
        file: null,
      }));
      this.getRemoteImages();
      this.$refs.uploadImageModalRef.show();
    },

    async handleOk(bvModalEvent) {
      bvModalEvent.preventDefault();
      await this.uploadImages();
    },

    onImagesSelected(event) {
      const files = event.target.files;
      if (!files || !files.length) return;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const reader = new FileReader();

        if (file.size > 5 * 1024 * 1024) {
          this.showToast(
            'Imagem muito grande. O tamanho máximo é 5MB.',
            'Erro',
            'danger',
          );
          continue;
        }

        if (!file.type.startsWith('image/')) {
          this.showToast(
            'Arquivo selecionado não é uma imagem.',
            'Erro',
            'danger',
          );
          continue;
        }

        const alreadyAdded = this.previewImages.some(
          img =>
            img.file &&
            img.file.name === file.name &&
            img.file.size === file.size &&
            img.file.lastModified === file.lastModified,
        );
        if (alreadyAdded) {
          this.showToast('Imagem já adicionada.', 'Erro', 'danger');
          continue;
        }

        reader.onload = e => {
          this.previewImages.push({
            base64Url: e.target.result,
            file,
          });
        };

        reader.readAsDataURL(file);
      }
    },

    toggleZoom(index) {
      if (this.zoomedIndex === index) {
        this.zoomedIndex = null;
      } else {
        this.zoomedIndex = index;
      }
    },

    downloadImage(index) {
      const imageItem = this.previewImages[index];
      if (!imageItem) return;

      const link = document.createElement('a');
      link.href = imageItem.base64Url;
      link.download = imageItem.file ? imageItem.file.name : 'imagem.png';
      link.click();
    },

    async removeImage(index) {
      if (this.previewImages[index].remoteUrl) {
        this.deleteRemoteImage(this.previewImages[index].remoteUrl);
      }
      this.previewImages.splice(index, 1);
    },

    async uploadImages() {
      if (!this.previewImages.length) {
        this.showToast('Selecione ao menos uma imagem', 'Erro', 'danger');
        return;
      }

      this.busyButton = true;
      try {
        const tasks = this.previewImages.map((item, index) => {
          if (!item.file) return null;
          return API.postFile(item.file).then(res => ({ index, res }));
        });

        const responses = await Promise.all(tasks);

        for (const r of responses) {
          if (!r) continue;
          if (!r.res.ok) {
            throw new Error('Falha ao fazer upload de uma ou mais imagens');
          }
          this.previewImages[r.index].remoteUrl = r.res.model;
        }

        const finalUrls = this.previewImages.map(item => item.remoteUrl);
        this.$emit('handleUploadedImages', finalUrls);

        this.showToast('Imagens enviadas com sucesso!', 'Sucesso', 'success');
        this.$refs.uploadImageModalRef.hide();
        this.previewImages = [];
      } catch (error) {
        this.showToast(error.message, 'Erro', 'danger');
      } finally {
        this.busyButton = false;
      }
    },

    async deleteRemoteImage(url) {
      if (!url) return;
      this.busyButton = true;
      try {
        const { ok, errors } = await API.post('/execute', {
          path: '/upload',
          method: 'delete',
          data: {
            fileUrl: url,
          },
        });

        if (!ok) {
          throw new Error(errors.message || 'Falha ao deletar imagem');
        }

        const remainingImages = this.previewImages
          .filter(item => item.remoteUrl !== url)
          .map(item => item.remoteUrl);

        this.$emit('handleUploadedImages', remainingImages);

        this.showToast('Deletada com sucesso!', 'Sucesso', 'success');
      } catch (err) {
        this.showToast(err.message, 'Erro', 'danger');
      } finally {
        this.busyButton = false;
      }
    },

    async getRemoteImages() {
      this.busyButton = true;
      try {
        const tasks = this.previewImages.map((item, index) => {
          if (!item.remoteUrl) return null;
          return API.post('/execute', {
            path: '/upload',
            method: 'get',
            data: { encodedFileUrl: encodeURIComponent(btoa(item.remoteUrl)) },
          }).then(res => ({ index, res }));
        });

        const results = await Promise.all(tasks);

        for (const r of results) {
          if (!r) continue;
          if (!r.res.ok) {
            throw new Error('Falha ao obter uma das imagens');
          }
          this.previewImages[r.index].base64Url = r.res.model;
        }

        this.showToast('Imagens obtidas com sucesso!', 'Sucesso', 'success');
      } catch (error) {
        this.showToast(error.message, 'Erro', 'danger');
      } finally {
        this.busyButton = false;
      }
    },

    showToast(message, title = 'Aviso', variant = 'info') {
      this.$bvToast.toast(message, { title, variant, solid: true });
    },
  },
};
</script>

<style scoped>
.images-list-container {
  max-height: 300px;
  overflow-y: auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
}

.preview-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  cursor: zoom-in;
}

.image-wrapper {
  width: 40%;
  position: relative;
}

.remove-image-btn {
  position: absolute;
  top: 0;
  right: 0;
  margin: 4px;
}

.download-image-btn {
  position: absolute;
  bottom: 0;
  right: 0;
  margin: 4px;
}

.image-wrapper-zoomed {
  position: absolute;
  z-index: 999;
  width: 100vw;
  top: 0;
  margin-top: -11vh;
  background-color: #0000008f;
}

.zoomed {
  width: 100%;
  height: unset;
  padding-inline: 5vw;
  padding-block: 5vh;
  cursor: zoom-out;
}

.hidden {
  display: none;
}
</style>
